import React, { useRef, useEffect, useState } from 'react'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { useMutation, gql } from '@apollo/client'
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem,
  Paper, TextField, Button, CircularProgress, Alert, InputAdornment
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid'
import RushmyFoodLogo from '../assets/img/rushmyfood-logo.png'
import ServerProfile from '../views/ServerProfile'
import ServerOrders from '../views/ServerOrders'
import { sendStaffPhoneOtp, verifyStaffPhoneOtp } from '../apollo'

const SEND_OTP = gql`${sendStaffPhoneOtp}`
const VERIFY_OTP = gql`${verifyStaffPhoneOtp}`

const DRAWER_WIDTH = 220

const NAV = [
  { path: '/server/profile', label: 'My Profile', icon: <PersonIcon fontSize="small" /> },
  { path: '/server/orders', label: 'My Orders', icon: <ReceiptLongIcon fontSize="small" /> },
]

// ── Phone Verification Wall ───────────────────────────────────────────────────
function PhoneVerificationWall({ session, onVerified }) {
  const [step, setStep] = useState(0) // 0=enter phone, 1=enter otp
  const [phone, setPhone] = useState(session.phone || '')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)

  const [mutateSendOtp] = useMutation(SEND_OTP)
  const [mutateVerifyOtp] = useMutation(VERIFY_OTP)

  const handleSend = async () => {
    if (!phone) { setError('Please enter your phone number'); return }
    setSending(true); setError('')
    try {
      const { data } = await mutateSendOtp({ variables: { phone } })
      if (data.sendStaffPhoneOtp.result) setStep(1)
      else setError(data.sendStaffPhoneOtp.message)
    } catch (e) { setError(e.message) }
    setSending(false)
  }

  const handleVerify = async () => {
    setError('')
    try {
      const { data } = await mutateVerifyOtp({ variables: { phone, otp } })
      if (data.verifyStaffPhoneOtp.result) {
        // Update session in localStorage
        const updated = { ...session, phoneVerified: true }
        localStorage.setItem('staff-session', JSON.stringify(updated))
        onVerified()
      } else {
        setError(data.verifyStaffPhoneOtp.message)
      }
    } catch (e) { setError(e.message) }
  }

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', p: 2
    }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: { xs: 3, sm: 4 }, maxWidth: 400, width: '100%', border: '1px solid #e2e8f0', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <PhoneAndroidIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#0f172a' }}>Verify Your Phone</Typography>
          <Typography sx={{ fontSize: '0.83rem', color: '#64748b', mt: 0.5 }}>
            {step === 0 ? 'Enter your phone number to receive a verification code.' : `Enter the 6-digit code sent to ${phone}`}
          </Typography>
        </Box>

        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Phone Number"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              fullWidth size="small"
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PhoneAndroidIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
              }}
            />
            {error && <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}
            <Button onClick={handleSend} disabled={sending} variant="contained" fullWidth
              sx={{ py: 1.3, borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              {sending ? <CircularProgress size={20} color="inherit" /> : 'Send Verification Code'}
            </Button>
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Verification Code"
              value={otp}
              onChange={e => setOtp(e.target.value)}
              fullWidth size="small"
              inputProps={{ maxLength: 6, style: { letterSpacing: '0.3em', fontSize: '1.2rem', textAlign: 'center' } }}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            {error && <Alert severity="error" sx={{ borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}
            <Button onClick={handleVerify} variant="contained" fullWidth
              sx={{ py: 1.3, borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              Verify & Continue
            </Button>
            <Button onClick={() => { setStep(0); setOtp(''); setError('') }} sx={{ textTransform: 'none', fontSize: '0.82rem', color: '#64748b' }}>
              Use a different number
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

// ── Main Server Layout ────────────────────────────────────────────────────────
function ServerLayout() {
  const history = useHistory()
  const location = useLocation()
  const divRef = useRef(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const raw = localStorage.getItem('staff-session')
  const session = raw ? JSON.parse(raw) : {}

  useEffect(() => {
    document.documentElement.scrollTop = 0
    if (divRef.current) divRef.current.scrollTop = 0
  }, [location.pathname])

  const handleLogout = () => {
    setAnchorEl(null)
    localStorage.removeItem('staff-session')
    history.push('/auth/login')
  }

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0f172a' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #1e293b' }}>
        <Box component="img" src={RushmyFoodLogo} alt="RushMyFood" sx={{ height: 28, filter: 'brightness(0) invert(1)' }} />
      </Box>
      <Box sx={{ px: 1.5, py: 1.5, borderBottom: '1px solid #1e293b' }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', mb: 0.5 }}>RESTAURANT</Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#e2e8f0', fontWeight: 600 }}>{session.restaurantName || '—'}</Typography>
      </Box>
      <List sx={{ flex: 1, px: 1, py: 1.5 }}>
        {NAV.map(({ path, label, icon }) => {
          const active = location.pathname === path
          return (
            <ListItem key={path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { history.push(path); setMobileOpen(false) }}
                sx={{ borderRadius: '10px', px: 1.5, py: 1, bgcolor: active ? '#1e3a5f' : 'transparent', '&:hover': { bgcolor: '#1e293b' } }}>
                <ListItemIcon sx={{ minWidth: 32, color: active ? '#60a5fa' : '#64748b' }}>{icon}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: active ? 700 : 500, color: active ? '#e2e8f0' : '#94a3b8' }} />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </Box>
  )

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Mobile AppBar */}
      <AppBar position="fixed" elevation={0}
        sx={{ display: { sm: 'none' }, bgcolor: '#0f172a', borderBottom: '1px solid #1e293b', zIndex: theme => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#e2e8f0', flex: 1 }}>Staff Portal</Typography>
          <IconButton onClick={e => setAnchorEl(e.currentTarget)} size="small">
            <Avatar sx={{ width: 30, height: 30, bgcolor: '#3b82f6', fontSize: '0.8rem' }}>
              {session.firstName?.[0]?.toUpperCase() || 'S'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer */}
      <Drawer variant="permanent"
        sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' } }} open>
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
        {drawerContent}
      </Drawer>

      {/* Top bar (desktop) */}
      <AppBar position="fixed" elevation={0}
        sx={{ display: { xs: 'none', sm: 'flex' }, ml: `${DRAWER_WIDTH}px`, width: `calc(100% - ${DRAWER_WIDTH}px)`, bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0', zIndex: theme => theme.zIndex.drawer - 1 }}>
        <Toolbar sx={{ minHeight: 56, justifyContent: 'flex-end', gap: 1.5 }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#475569' }}>
            {session.firstName} {session.lastName} · <strong>{session.role}</strong>
          </Typography>
          <IconButton onClick={e => setAnchorEl(e.currentTarget)} size="small">
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#3b82f6', fontSize: '0.85rem' }}>
              {session.firstName?.[0]?.toUpperCase() || 'S'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
        PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 160 } }}>
        <MenuItem onClick={handleLogout} sx={{ fontSize: '0.85rem', color: '#ef4444', borderRadius: '8px', mx: 0.5, my: 0.25 }}>
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>Sign Out
        </MenuItem>
      </Menu>

      {/* Content */}
      <Box ref={divRef} sx={{ ml: { xs: 0, sm: `${DRAWER_WIDTH}px` }, pt: { xs: 7, sm: 7 }, minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <Switch>
          <Route path="/server/profile" component={ServerProfile} />
          <Route path="/server/orders" component={ServerOrders} />
        </Switch>
      </Box>
    </Box>
  )
}

export default ServerLayout
